<script setup>
import store from '@/store'
import { ref, computed, getCurrentInstance, onMounted, watch } from 'vue'
import { scrollTo } from 'vue-scrollto'
import { useLocalStorage } from '@vueuse/core'

import { cloneDeep, isNumber, get } from 'lodash-es'
import { SERVICES_URL, getErrorMessage, SEARCH_URL } from '@/main'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import KetcherEditor from '@/components/ketcher-editor/KetcherEditor.vue'
import ReportControls from '@/components/ReportControls.vue'
import QueryParamsMenu from './QueryParamsMenu.vue'
import SearchOptions from './SearchOptions.vue'
import SearchReport from './SearchReport.vue'
import { defaultSearchOptions, defaultPageOptions, defaultFilterState } from '@/components/search/options'
import { constructQuery } from '@/components/search/constructQuery'
import TableCardLayout from '@/components/report-page-layouts/TableCardLayout.vue'
import { joinWithBr } from '@/utils/html.js'
import TristateFilter from '@/components/TristateFilter.vue'
import { showErrorToast } from '@/utils/toast'

const { proxy } = getCurrentInstance()

const inProgress = ref(false)

const constructedQuery = ref(null)
const searchResult = computed({
  get: () => store.getters.SEARCH_RESULT ?? null,
  set: (value) => store.commit('SET_SEARCH_RESULT', value),
})
const pageOptions = ref(cloneDeep(defaultPageOptions))
const searchOptions = useLocalStorage('search.search-options', defaultSearchOptions, {
  mergeDefaults: true,
})
const filterState = ref(cloneDeep(defaultFilterState))
const optionsErrors = ref([])

const totalRecords = computed(() => searchResult.value?.totalRecordsCount)
const totalPages = computed(() => Math.ceil(totalRecords.value / pageOptions.value.pageSize) || 1)

watch(totalPages, (newVal) => {
  if (pageOptions.value.currentPage > newVal) pageOptions.value.currentPage = newVal
})

const structureFromKetcher = useLocalStorage('search.structure', null, {
  mergeDefaults: true,
})

const reportControlsStatus = computed(() => {
  // const readyToSearch =
  //   !!structureFromKetcher.value ||
  //   searchOptions.value.searchType === 'FEATURES' ||
  //   searchOptions.value.searchType === 'HAZARD' ||
  //   (searchOptions.value.searchType === 'FORMULA' && !!searchOptions.value.formula) ||
  //   (searchOptions.value.searchType === 'MASS' && searchOptions.value.massSearchParamsValid)

  if (inProgress.value) return 'loading'

  if (searchResult.value?.records?.length) {
    return 'stale'
    // return readyToSearch ? 'stale' : 'fresh'
  }
  return 'ready'
  // return readyToSearch ? 'ready' : 'invalid'
})

const currentAbortController = ref(null)
const abortSearch = () => {
  if (currentAbortController.value) {
    try {
      currentAbortController.value.abort()
    } catch (e) {}
    currentAbortController.value = null
  }
}

const onResetSearch = () => {
  abortSearch()

  searchResult.value = store.commit('RESET_SEARCH_RESULT')
  pageOptions.value = cloneDeep(defaultPageOptions)
  filterState.value = cloneDeep(defaultFilterState)
  constructedQuery.value = null
}

const filterResponseMap = {
  'filter-stereo': 'stereo',
  'filter-chiral': 'chiral',
  'filter-isotopes': 'isotopes',
  'filter-charged': 'charged',
  'filter-multicomponent': 'multicomponent',
  'filter-radicals': 'radicals',
  'filter-salts': 'salts',
  'filter-polymers': 'polymers',
  'filter-sgroups': 'sgroups',
  'filter-toxicity': 'toxicity',
}

const updateFilterState = (features) => {
  if (!features) return

  const updatedFilters = filterState.value.map((filter) => {
    const responseId = get(filterResponseMap, filter.value)
    const responseValue = get(features, responseId)

    if (responseId && isNumber(responseValue)) {
      return { ...filter, count: responseValue }
    }
    return filter
  })
  // .filter((filter) => !searchOptions.value.features.includes(filter.value))

  filterState.value = [...updatedFilters]
}

const isFilterStateActive = computed(() => {
  return filterState.value.some((filter) => filter.state != null)
})
const showTristateFilter = computed(
  () => isFilterStateActive.value || filterState.value.some((filter) => filter.count != null),
)

const applyFiltersAndSearch = (updatedFilters) => {
  filterState.value = [...updatedFilters]
  searchByStructure()
}

const resetFilterStateAndSearch = () => {
  filterState.value = cloneDeep(defaultFilterState)
  searchByStructure()
}

const parseSearchError = (error) => {
  if (searchOptions.value.searchType === 'FORMULA' && typeof error?.json === 'function') {
    error.json().then((data) => {
      if (data.error) return data.error
    })
  }

  const errorText = getErrorMessage(error)
  if (typeof errorText === 'string') {
    if (errorText.includes('504')) {
      return 'The search is taking too long and was stopped. Try narrowing your search criteria.'
    }
    if (errorText.includes('502')) {
      return 'The search service is currently unavailable. Please try again later.'
    }
  }

  return errorText
}

const searchByStructure = () => {
  abortSearch()

  // if (searchOptions.value.searchType === 'FORMULA') sortBy.value = 'mass'
  // if (searchOptions.value.searchType === 'HAZARD') sortBy.value = 'toxicity'

  if (optionsErrors.value.length) {
    const errorMessage = joinWithBr(
      optionsErrors.value.map((err) => {
        return `${err.label}: ${err.messages}`
      }),
    )
    showErrorToast(errorMessage)

    return
  }

  if (searchOptions.value.searchType === 'MASS') {
    if (!searchOptions.value.minMass || !searchOptions.value.maxMass) {
      showErrorToast('Both mass parameters must be provided')
      return
    }
  }

  if (!structureFromKetcher.value) {
    if (searchOptions.value.searchType === 'FORMULA') {
      if (!searchOptions.value.formula) {
        showErrorToast('No formula or structure provided')
        return
      }
    }

    if (!['FEATURES', 'HAZARD', 'MASS', 'FORMULA'].includes(searchOptions.value.searchType)) {
      showErrorToast('There is no structure drawn')
      return
    }
  }

  const abortController = new AbortController()
  currentAbortController.value = abortController

  constructedQuery.value = constructQuery({
    searchOptions: searchOptions.value,
    pageOptions: pageOptions.value,
    filterState: filterState.value,
    structureFromKetcher: structureFromKetcher.value,
  })

  // return

  inProgress.value = true
  return proxy.$axios
    .post(SEARCH_URL, constructedQuery.value, { signal: abortController.signal })
    .then((response) => {
      updateFilterState(response.data?.features)
      searchResult.value = response.data
      scrollTo('#search-report', 500, { easing: 'ease-in-out' })
    })
    .catch((error) => {
      if (
        error?.name === 'AbortError' ||
        error?.code === 'ERR_CANCELED' ||
        (error?.message || '').toLowerCase().includes('canceled')
      ) {
        return
      }

      const errorMessage = parseSearchError(error)
      showErrorToast(errorMessage)
    })
    .finally(() => {
      inProgress.value = false
      currentAbortController.value = null
    })
}

const isKetcherDisabled = computed(() => {
  return (
    searchOptions.value.searchType === 'FEATURES' || searchOptions.value.searchType === 'HAZARD'
    // ||
    // (searchOptions.value.searchType === 'MASS' && (!searchOptions.value.minMass || !searchOptions.value.maxMass))
  )
})

watch(
  pageOptions,
  () => {
    searchByStructure()
  },
  { deep: true },
)

const loadChemical = (item) => {
  proxy.$axios.get(`${SERVICES_URL}/layout?smiles=${encodeURIComponent(item.smiles)}`).then((response) => {
    structureFromKetcher.value = response.data
  })
}

const stopButtonId = `search-stop-${Math.random().toString(36).slice(2, 8)}`
const isXsScreen = computed(() => proxy.$vuetify.breakpoint.xs)

onMounted(() => {
  store.commit('SET_VIEW_STRUCTURES', false)
})
</script>

<template>
  <TableCardLayout>
    <template #table-controls>
      <div
        class="d-flex gap-6"
        :class="isXsScreen ? 'flex-column' : 'flex-row'"
      >
        <div
          class="d-flex flex-column flex-1 tw-min-w-0"
          style="max-width: 50rem"
        >
          <KetcherEditor
            v-model="structureFromKetcher"
            :disabled="isKetcherDisabled"
            resolve
            show-found
          />
        </div>

        <div
          class="d-flex flex-column flex-1 tw-min-w-0 gap-2"
          style="max-width: 30rem"
        >
          <SearchOptions
            v-model="searchOptions"
            :filters="defaultFilterState"
            @errors="(val) => (optionsErrors = [...val])"
          />
          <v-spacer />

          <div class="align-self-end">
            <ReportControls
              :status="reportControlsStatus"
              calculate-label="Search"
              clear-label="Reset"
              calculate-tooltip="Run search"
              clear-tooltip="Reset search results"
              calculate-aria-label="Search"
              clear-aria-label="Reset"
              :ui="{ calculate: 'primary' }"
              @calculate="resetFilterStateAndSearch"
              @clear="onResetSearch"
            >
              <template
                v-if="inProgress"
                #before-clear
              >
                <v-btn
                  :id="stopButtonId"
                  aria-label="Stop search"
                  @click="abortSearch"
                >
                  <v-icon>mdi-stop</v-icon>
                </v-btn>

                <v-tooltip
                  top
                  v-bind="defaultVTooltipProps"
                  :activator="`#${stopButtonId}`"
                >
                  <span>Stop search</span>
                </v-tooltip>
              </template>
            </ReportControls>
          </div>
        </div>
      </div>
    </template>

    <div class="d-flex flex-column gap-2 w-100">
      <div
        v-if="searchResult"
        class="d-flex flex-row align-items-center gap-2"
      >
        <template v-if="!inProgress">
          <template v-if="totalRecords">
            <div>
              Found
              <span class="badge badge-info">{{ searchResult.totalRecordsCount }}</span>
              records containing elements:
              <em>{{ Object.keys(searchResult.features.elements).join(', ') }}</em>
            </div>

            <div v-if="totalRecords && searchResult?.appliedMin != null">
              <v-icon style="color: #ffc107">mdi-alert-circle-outline</v-icon>
              The min. similarity parameter was reduced to
              <span class="badge badge-warning">
                {{ searchResult.appliedMin.toFixed(3) }}
              </span>
              to find the specified minimum number of records
              <span class="badge badge-secondary">({{ searchOptions.minNumOfStructures }})</span>
            </div>
          </template>

          <div v-else>Nothing found</div>
        </template>

        <QueryParamsMenu
          v-if="constructedQuery"
          :query-body="constructedQuery"
        />
      </div>

      <div id="search-report" />
      <template v-if="totalRecords || showTristateFilter">
        <div
          class="d-flex gap-2"
          :class="[
            isXsScreen ? 'flex-column-reverse' : 'flex-row align-items-center',
            showTristateFilter ? 'justify-content-between' : 'justify-content-end',
          ]"
        >
          <TristateFilter
            v-if="showTristateFilter"
            :value="filterState"
            @input="(val) => applyFiltersAndSearch(val)"
            size="sm"
            class="d-flex flex-wrap gap-x-2 gap-y-1"
            button-class="flex-grow-0"
          />

          <v-select
            v-if="totalRecords"
            label="Sort"
            v-model="pageOptions.sortBy"
            :items="[
              ...(searchOptions.searchType !== 'FORMULA' && searchOptions.searchType !== 'HAZARD'
                ? [{ text: 'Similarity', value: 'similarity' }]
                : []),
              { text: 'Mass', value: 'mass' },
              { text: 'Toxicity', value: 'toxicity' },
            ]"
            dense
            outlined
            hide-details
            style="max-width: 10rem"
          >
            <template #append>
              <v-btn
                icon
                @click.stop="onSortToggle"
                @mousedown.stop.prevent
                aria-label="Sort direction"
                @click="pageOptions.sortDirection = pageOptions.sortDirection === 'asc' ? 'desc' : 'asc'"
              >
                <v-icon
                  v-if="pageOptions.sortDirection === 'asc'"
                  title="Ascending"
                >
                  mdi-sort-ascending
                </v-icon>
                <v-icon
                  v-else
                  title="Descending"
                >
                  mdi-sort-descending
                </v-icon>
              </v-btn>
            </template>
          </v-select>
        </div>

        <SearchReport
          v-if="totalRecords"
          :search-result="searchResult"
          :loading="inProgress"
          @loadChemical="loadChemical"
        />

        <div
          v-if="totalRecords"
          class="d-flex flex-row align-items-center gap-2 justify-content-between"
        >
          <v-pagination
            v-model="pageOptions.currentPage"
            :length="totalPages"
            total-visible="5"
            dense
          />

          <v-select
            v-model="pageOptions.pageSize"
            :items="[
              { text: 50, value: 50 },
              { text: 100, value: 100 },
              { text: 150, value: 150 },
            ]"
            label="Page size"
            style="max-width: 5rem"
            hide-details
            dense
          />
        </div>
      </template>
    </div>
  </TableCardLayout>
</template>
